/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo, useCallback, type ReactElement, type ReactNode, type Ref } from 'react';
import { SpotlightTarget } from '@atlassian/jira-spotlight/src/ui/SpotlightTarget.tsx';
import { ROADMAP_INTRODUCTION_SPOTLIGHT } from '@jira/polaris__lib-onboarding-flows/src/controllers/constants.tsx';
import { RoadmapIntroductionSpotlight } from '@jira/polaris__lib-onboarding-flows/src/ui/collections/tour/roadmap-introduction-spotlight/index.tsx';
import { getCollectionsPageUrl } from '@jira/polaris__component-collections/src/getCollectionsPageUrl.tsx';
import { PremiumLozenge } from '@jira/polaris__component-premium-lozenge/src/PremiumLozenge.tsx';
import { SECTION_ITEM_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { useIsJpdProjectView } from '../../../common/utils/router.tsx';
import { Footer } from '../../../common/ui/footer/index.tsx';

export type RoadmapsStaticProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	children?: ReactNode;
	isExpanded: boolean;
	onExpansionToggle?: (isExpanded: boolean) => void;
	triggerRef?: Ref<HTMLButtonElement | HTMLAnchorElement>;
};

const RoadmapsStaticGSN = memo((props: RoadmapsStaticProps) => {
	const {
		actionsOnHover,
		children,
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		isExpanded,
		onExpansionToggle,
		onExpandFlyoutToggle,
		triggerRef,
		visualContentRef,
	} = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ROADMAPS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const isJpdProjectView = useIsJpdProjectView();

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			onExpansionToggle?.(hasExpanded);

			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.ROADMAPS,
					sectionItem: SECTION_ITEM_ROADMAPS,
				});
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpansionToggle, onExpandFlyoutToggle, sendSectionItemAnalyticsEvent],
	);

	return (
		<SpotlightTarget name={ROADMAP_INTRODUCTION_SPOTLIGHT} isFullWidth>
			<Nav4ExpandableMenuItem
				dropTargetRef={visualContentRef}
				dropIndicator={dropIndicator}
				isDragging={isDragging}
				isExpanded={isExpanded}
				menuId={MENU_ID_ROADMAPS}
				onExpansionToggle={handleExpansionToggle}
			>
				<ExpandableMenuItemTrigger
					interactionName="nav4-sidebar-roadmaps-default"
					actionsOnHover={actionsOnHover}
					aria-label={formattedMessage}
					elemBefore={icon}
					elemAfter={<PremiumLozenge />}
					hasDragIndicator={hasDragIndicator}
					isDragging={isDragging}
					ref={getRefs(draggableRef, triggerRef)}
					testId={getTestId(MENU_ID_ROADMAPS)}
				>
					{formattedMessage}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					{children}
					<Footer />
				</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>
			{isJpdProjectView && (
				<RoadmapIntroductionSpotlight
					collectionsUrl={getCollectionsPageUrl()}
					placement="right middle"
				/>
			)}
		</SpotlightTarget>
	);
});

const RoadmapsStaticNav4 = memo((props: RoadmapsStaticProps) => {
	const { isExpanded, onExpansionToggle, actionsOnHover, children, triggerRef } = props;
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ROADMAPS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const isJpdProjectView = useIsJpdProjectView();

	return (
		<SpotlightTarget name={ROADMAP_INTRODUCTION_SPOTLIGHT} isFullWidth>
			<Nav4ExpandableMenuItem
				isExpanded={isExpanded}
				menuId={MENU_ID_ROADMAPS}
				onExpansionToggle={(hasExpanded) => {
					onExpansionToggle?.(hasExpanded);

					if (hasExpanded) {
						sendSectionItemAnalyticsEvent({
							menuId: L1_MENU_ID.ROADMAPS,
							sectionItem: SECTION_ITEM_ROADMAPS,
						});
					}
				}}
			>
				<ExpandableMenuItemTrigger
					interactionName="nav4-sidebar-roadmaps-default"
					actionsOnHover={actionsOnHover}
					aria-label={formattedMessage}
					elemBefore={icon}
					elemAfter={<PremiumLozenge />}
					ref={getRefs(null, triggerRef)}
					testId={getTestId(MENU_ID_ROADMAPS)}
				>
					{formattedMessage}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					{children}
					<Footer />
				</ExpandableMenuItemContent>
			</Nav4ExpandableMenuItem>
			{isJpdProjectView && (
				<RoadmapIntroductionSpotlight
					collectionsUrl={getCollectionsPageUrl()}
					placement="right middle"
				/>
			)}
		</SpotlightTarget>
	);
});

export const RoadmapsStatic: React.FC<RoadmapsStaticProps> = componentWithFG(
	'navx-global-side-nav',
	RoadmapsStaticGSN,
	RoadmapsStaticNav4,
);

function getRefs(
	draggableRef: Ref<HTMLButtonElement | HTMLAnchorElement> | undefined,
	triggerRef: Ref<HTMLButtonElement | HTMLAnchorElement> | undefined,
) {
	return draggableRef ? mergeRefs(draggableRef, triggerRef || null) : triggerRef;
}
